
import { Tables } from "@/integrations/supabase/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { GridLayout } from "../GridLayout";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

interface DeviceHistoryProps {
  device_id: string;
  initialData?: {
    device_name: string | null;
    device_description: string | null;
  } | null;
}

export const DeviceHistory = ({ device_id, initialData }: DeviceHistoryProps) => {
  const queryClient = useQueryClient();
  const { data: device } = useQuery({
    queryKey: ["device-details", device_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("device_details")
        .select("*")
        .eq("device_id", device_id)
        .single();

      if (error) throw error;
      return data as Tables<"device_details">;
    },
    initialData: initialData ? (() => {
      // Create a properly typed default value that matches Tables<"device_details"> structure
      const defaultDevice: Tables<"device_details"> = {
        device_id,
        device_name: initialData.device_name,
        device_description: initialData.device_description,
        battery: null,
        boundary_boxes: [],
        camera_config: {
          scheduleStart: "0",
          scheduleEnd: "0",
          cameraSaturation: 0,
          cameraEV: 0,
          cameraContrast: 0,
          cameraBrightness: 0,
          cameraAbsoluteExposure: "0",
          cameraISOSensitivity: "0",
          withinScheduleSleepTime: 3600000,
          cameraSetAutoExposure: 1,
          cameraSetAutoISOSensitive: 1
        },
        cellular: null,
        current_hdop: null,
        enable_production_test: false,
        device_firmware: null,
        device_last_updated: null,
        device_status: null,
        gnss: null,
        imagekit: null,
        last_synced_at: new Date().toISOString(),
        lat_wgs84: null,
        location: null,
        long_wgs84: null,
        notification: null,
        past_images: [],
        safe_search: null,
        device_serial: null,
        images_allowed: null,
        images_current: null,
        images_overflow: null,
        notification_ai_model: null,
        group_name: null,
        group_reference: null,
        timezone: null,
        organization_id: null,
        ambient_lux: null,
        enable_sponsorship: null,
        enable_public_view: null,
        enable_notifications: null,
        enable_surveillance_mode: null,
        notification_email: null,
        notification_question: null,
        location_enabled: null,
        on_demand_user_id: null,
        group_id: null,
        camera_enable_12v: null,
        camera_select: null,
        camera_light_level: null,
        ai_workflow: 'quick_reasoning',
        notification_data_tags: null,
        notification_data: null,
        test_image_url: null,
        camera_image_quality: null,
        max_sleep_time: null,
        device_privacy_level: 1,
        device_operational_days: 'Monday,Tuesday,Wednesday,Thursday,Friday,Saturday,Sunday',
        device_event_dates: '',
        event_schedule_sleep_time: 3600000,
        images_stopped_at: null,
        images_stopped_by: null,
        stopped_config_snapshot: null,
      };
      return defaultDevice;
    })() : undefined
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["device-details", device_id] });
    queryClient.invalidateQueries({ queryKey: ["device", device_id] });
    toast.success("Refreshing device data...");
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">
          {device?.device_name || "Device Images"}
        </h1>
        <Button
          onClick={handleRefresh}
          variant="outline"
          size="sm"
          className="gap-2 text-xs"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </Button>
      </div>
      <GridLayout 
        deviceId={device_id} 
        deviceName={device?.device_name || undefined}
        deviceDescription={device?.device_description || undefined}
      />
    </div>
  );
};
